import { useMembershipAskModal } from '#src/app/shared/components/membershipAsk';
import { useIsGuestEmailExistingMember } from '#src/app/shared/hooks/membership';
import type { PaymentType } from '#src/app/shared/utils/payments';
import { paymentMethod } from '#src/app/shared/utils/payments';

import { usePromoteFormData } from '../../../formContext/formData';
import { useEnforceMembershipAmount } from '../useEnforceMembershipAmount';
import { useIsMembershipEligible } from '../useIsMembershipEligible';
import { useSaveBraintreePaymentTemporarily } from '../useSaveBraintreePaymentTemporarily';
import { useUserInfo } from '../useUserInfo';

import { useTokenizationData } from './hooks/useTokenizationData';
import { useTrackingProperties } from './hooks/useTrackingProperties';

type Props = {
	token?: string | null;
	accountId?: string | null;
	paymentType: PaymentType;
};

type Result = { accepted: boolean; amount: number } | undefined;

export function usePPMU(): (props: Props) => Promise<Result> {
	const isEligibleForMembership = useIsMembershipEligible();

	const openModal = useMembershipAskModal();

	// usingSavedPaymentMethod is always false in PSF (not supported)
	const usingSavedPaymentMethod = false;

	const {
		currency,
		membershipProgram,
		petition: { id: petitionId },
		featureConfigs: { useDonationLanguage },
		user,
	} = usePromoteFormData();

	const isGuestEmailExistingMember = useIsGuestEmailExistingMember();

	const getMembershipAmount = useEnforceMembershipAmount();

	const properties = useTrackingProperties();

	// useUpdateTrackingEventsWithGuestData skipped - not available in PSF
	const userInfo = useUserInfo();
	const savingBraintreePaymentTemporarilyEnabled = useSaveBraintreePaymentTemporarily();
	const getTokenizationData = useTokenizationData();
	// useFetchLocalizedPPMUHeadlineData skipped - assume undefined
	const ppmuLocalizedHeadlineData = undefined;

	return async function displayModal({ token, accountId, paymentType }): Promise<Result> {
		if (!isEligibleForMembership) return undefined;

		if (await isGuestEmailExistingMember(user.email)) return undefined;

		const { amount, minAmount, maxAmount, minSliderAmount, maxSliderAmount, sliderStep, sliderLayout } =
			getMembershipAmount();

		const modal = openModal({
			amount,
			...userInfo(paymentType),
			currencyCode: currency.code,
			petitionId,
			useDonationLanguage,
			tokenizationData: getTokenizationData({ paymentType, token, accountId }),
			usingSavedPaymentMethod,
			gateway: paymentMethod(paymentType).gateway,
			// membershipProgram.id should exist when isEligibleForMembership is true
			membershipProgramId: membershipProgram?.id ?? '',
			paymentType,
			properties: {
				...properties,
				payment_type: paymentType,
			},
			minAmount,
			maxAmount,
			// useUpdatedName skipped - useUpdateTrackingEventsWithGuestData not available
			useUpdatedName: false,
			savingBraintreePaymentTemporarilyEnabled,
			ppmuLocalizedHeadlineData,
			sliderConfig: {
				minSliderAmount: minSliderAmount ?? minAmount,
				maxSliderAmount: maxSliderAmount ?? maxAmount,
				sliderStep: sliderStep ?? 1,
				sliderLayout,
			},
			currencyExchangeRate: 1,
		});

		return modal.waitForResult();
	};
}
